import { useEffect, useRef, useState } from 'react';
import { archivedPdfUrl, getStatementArchivedPdf, getStatementPreviewPdf, statementArchivedPdfUrl, statementPreviewPdfUrl } from '@/api/client';
import { Icon } from '@/components/Icon';
import { useFormFactor } from '@/lib/formFactor';
import { generateStatementPdf, legacy } from './api';
import type { StatementPayload } from './types';
import s from './StatementPdfDialog.module.css';

const detailOptionLabels = [
  '套装展开成组件明细',
  '显示未发货部分（列出但不计金额）',
  '显示破损/退货冲减行',
  '每张订单显示小计',
  '显示客户单号',
] as const;

export function StatementPdfDialog({ data, canGenerate, onClose, onGenerated }: {
  data: StatementPayload; canGenerate: boolean; onClose: () => void; onGenerated: () => void;
}) {
  const ff = useFormFactor();
  const hasGoodsSnapshot = data.businessDocuments.length > 0 && data.businessDocuments.every((line) => Array.isArray(line.goods));
  const [summaryPdfPath, setSummaryPdfPath] = useState(data.pdfFileUrl || '');
  const [detailPdfPath, setDetailPdfPath] = useState(data.detailPdfFileUrl || '');
  const [variant, setVariant] = useState<'summary' | 'detail'>(() => data.detailPdfFileUrl || hasGoodsSnapshot ? 'detail' : 'summary');
  const [detailOptions, setDetailOptions] = useState([true, true, true, true, false]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [pdfAsset, setPdfAsset] = useState<{ url: string; file: File } | null>(null);
  const [pdfRetry, setPdfRetry] = useState(0);
  const [previewPage, setPreviewPage] = useState(1);
  const [actualPages, setActualPages] = useState(0);
  const [previewError, setPreviewError] = useState('');
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const pdfPath = variant === 'detail' ? detailPdfPath : summaryPdfPath;
  const canDetail = data.businessDocuments.every((line) => Array.isArray(line.goods));
  const locked = data.status === 'LOCKED';
  const preview = data.status === 'DRAFT' || data.status === 'CONFIRMED';
  const pdfUrl = locked ? pdfPath && data.id ? statementArchivedPdfUrl(data.id, variant) : null : preview && data.id ? statementPreviewPdfUrl(data.id, variant) : null;
  const readOnly = import.meta.env.VITE_READONLY !== '0';
  const filename = preview ? `对账单-${data.statementNo || data.id}${variant === 'detail' ? '-货物明细' : ''}-预览.pdf`
    : variant === 'detail' ? data.detailPdfFileName || `对账单-${data.statementNo || data.id}-货物明细.pdf` : data.pdfFileName || `对账单-${data.statementNo || data.id}.pdf`;
  const readyFile = pdfAsset?.url === pdfUrl && pdfAsset.file.name === filename ? pdfAsset.file : null;
  const legacyUrl = legacy.statements({ scope: data.scope, partnerId: data.partner.id, month: data.period, dateBasis: data.dateBasis });
  const detailRowCount = data.businessDocuments.reduce((count, line) => count + (line.goods?.length ?? 1), 0);
  const displayedRows = variant === 'detail' ? detailRowCount : data.businessDocuments.length;
  const estimatedPages = Math.max(1, Math.ceil((displayedRows + (variant === 'detail' ? 4 : 2)) / 8));
  const pageCount = actualPages || estimatedPages;
  const goodsSnapshot = data.businessDocuments.flatMap((document) => document.goods || []);
  const shippedSnapshotComplete = goodsSnapshot.length > 0 && goodsSnapshot.every((good) =>
    good.adjustmentKind || good.shippedQuantity !== undefined && good.shippedQuantity !== null,
  );
  const adjustmentCount = goodsSnapshot.filter((good) => good.adjustmentKind || good.amount < 0).length;

  useEffect(() => {
    setPreviewPage(1);
  }, [variant]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!readyFile) { setActualPages(0); return; }
    let cancelled = false;
    let documentTask: import('pdfjs-dist').PDFDocumentLoadingTask | undefined;
    let renderTask: { cancel: () => void; promise: Promise<void> } | undefined;
    let resizeObserver: ResizeObserver | undefined;
    let pdfDocument: import('pdfjs-dist').PDFDocumentProxy | undefined;
    let renderVersion = 0;
    let lastSize = '';
    setPreviewError('');
    const drawPage = async () => {
      if (!canvas) return;
      const surface = canvas.parentElement;
      const bounds = surface?.getBoundingClientRect();
      if (!pdfDocument || !bounds || bounds.width < 100 || bounds.height < 100 || cancelled) return;
      const size = `${Math.floor(bounds.width)}x${Math.floor(bounds.height)}:${previewPage}`;
      if (size === lastSize) return;
      lastSize = size;
      const version = ++renderVersion;
      renderTask?.cancel();
      try {
        const page = await pdfDocument.getPage(Math.min(previewPage, pdfDocument.numPages));
        if (cancelled || version !== renderVersion || !canvasRef.current) return;
        const base = page.getViewport({ scale: 1 });
        const inset = ff === 'phone' ? 16 : 36;
        const scale = Math.min((bounds.width - inset) / base.width, (bounds.height - inset) / base.height);
        const ratio = window.devicePixelRatio || 1;
        const viewport = page.getViewport({ scale: scale * ratio });
        canvas.width = Math.ceil(viewport.width);
        canvas.height = Math.ceil(viewport.height);
        canvas.style.width = `${Math.ceil(viewport.width / ratio)}px`;
        canvas.style.height = `${Math.ceil(viewport.height / ratio)}px`;
        const context = canvas.getContext('2d');
        if (!context) throw new Error('无法创建 PDF 预览画布');
        renderTask = page.render({ canvas, canvasContext: context, viewport });
        await renderTask.promise;
      } catch (cause) {
        const name = cause instanceof Error ? cause.name : '';
        if (!cancelled && name !== 'RenderingCancelledException') setPreviewError(cause instanceof Error ? cause.message : 'PDF 页面预览失败');
      }
    };
    const load = async () => {
      try {
        const [{ getDocument, GlobalWorkerOptions }, worker] = await Promise.all([
          import('pdfjs-dist'), import('pdfjs-dist/build/pdf.worker.min.mjs?url'),
        ]);
        if (cancelled) return;
        GlobalWorkerOptions.workerSrc = worker.default;
        const bytes = new Uint8Array(await readyFile.arrayBuffer());
        if (cancelled) return;
        documentTask = getDocument({ data: bytes });
        pdfDocument = await documentTask.promise;
        if (cancelled) return;
        setActualPages(pdfDocument.numPages);
        if (canvas) {
          await drawPage();
          resizeObserver = new ResizeObserver(() => { void drawPage(); });
          if (canvas.parentElement) resizeObserver.observe(canvas.parentElement);
        }
      } catch (cause) {
        if (!cancelled) setPreviewError(cause instanceof Error ? cause.message : 'PDF 页面预览失败');
      }
    };
    void load();
    return () => {
      cancelled = true;
      resizeObserver?.disconnect();
      renderVersion++;
      renderTask?.cancel();
      void documentTask?.destroy();
    };
  }, [ff, previewPage, readyFile]);

  useEffect(() => {
    const key = (event: KeyboardEvent) => { if (event.key === 'Escape' && !busy) onClose(); };
    window.addEventListener('keydown', key);
    return () => window.removeEventListener('keydown', key);
  }, [busy, onClose]);

  useEffect(() => {
    if (!pdfUrl || !data.id) return;
    let active = true;
    setPdfAsset(null);
    setError('');
    void (preview ? getStatementPreviewPdf(data.id, variant) : getStatementArchivedPdf(data.id, variant))
      .then((bytes) => { if (active) setPdfAsset({ url: pdfUrl, file: new File([bytes], filename, { type: 'application/pdf' }) }); })
      .catch((cause) => { if (active) setError(cause instanceof Error ? cause.message : '准备 PDF 失败，请重试。'); });
    return () => { active = false; };
  }, [data.id, filename, pdfRetry, pdfUrl, preview, variant]);

  const generate = async () => {
    if (!data.id || !locked || !canGenerate || busy || readOnly) return;
    setBusy(true); setError('');
    try {
      const result = await generateStatementPdf(data.id, variant);
      if (!archivedPdfUrl(result.pdfFileUrl)) throw new Error('已生成 PDF，但返回的归档地址无效；请刷新对账单确认。');
      if (variant === 'detail') setDetailPdfPath(result.pdfFileUrl);
      else setSummaryPdfPath(result.pdfFileUrl);
      onGenerated();
    } catch (cause) { setError(cause instanceof Error ? cause.message : '生成 PDF 失败，请刷新后检查。'); }
    finally { setBusy(false); }
  };

  const saveFile = (file: File) => {
    const url = URL.createObjectURL(file);
    const link = document.createElement('a');
    link.href = url; link.download = file.name; document.body.append(link); link.click(); link.remove();
    window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
  };

  const download = (share: boolean) => {
    if (!readyFile || busy) return;
    setError('');
    if (share && navigator.share && (!navigator.canShare || navigator.canShare({ files: [readyFile] }))) {
      void navigator.share({ files: [readyFile], title: readyFile.name }).catch((cause) => {
        if ((cause as Error).name !== 'AbortError') saveFile(readyFile);
      });
    } else saveFile(readyFile);
  };

  const openPdf = () => {
    if (!readyFile || busy) return;
    const url = URL.createObjectURL(readyFile);
    window.open(url, '_blank', 'noopener,noreferrer');
    window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
  };

  const toggleDetailOption = (index: number) => setDetailOptions((current) => current.map((value, i) => i === index ? !value : value));

  if (ff === 'tablet') return <div className={`${s.scrim} ${s.tabletScrim}`} onMouseDown={(event) => { if (event.target === event.currentTarget && !busy) onClose(); }}>
    <section className={`${s.dialog} ${s.tabletDialog}`} role="dialog" aria-modal="true" aria-label={`导出对账单 PDF ${data.statementNo || ''}`}>
      <div className={s.tabletFrame}>
        <header className={s.tabletHeader}>
          <div className={s.tabletTitle}><strong>导出对账单 PDF</strong><small>{data.statementNo} · {data.status === 'DRAFT' ? '草稿' : data.status === 'CONFIRMED' ? '已确认' : '已锁账'}</small></div>
          <button type="button" className={s.tabletClose} aria-label="关闭" onClick={onClose} disabled={busy}>×</button>
        </header>
        <div className={s.tabletBody}>
          <aside className={s.tabletSettings}>
            <section><h3>版本</h3><div className={s.tabletRadios} role="radiogroup" aria-label="PDF 版式">
              <button type="button" role="radio" aria-checked={variant === 'detail'} className={variant === 'detail' ? s.tabletRadioActive : ''} disabled={!canDetail} onClick={() => setVariant('detail')}>明细版</button>
              <button type="button" role="radio" aria-checked={variant === 'summary'} className={variant === 'summary' ? s.tabletRadioActive : ''} onClick={() => setVariant('summary')}>汇总版</button>
            </div></section>
            {variant === 'detail' && <section><h3>明细版选项</h3><div className={s.tabletDetailOptions}>{detailOptionLabels.map((label, index) => <button key={label} type="button" aria-pressed={detailOptions[index]} disabled={locked} onClick={() => toggleDetailOption(index)}><span className={`${s.tabletSwitch} ${detailOptions[index] ? s.tabletSwitchOn : ''}`} /><span>{label}</span></button>)}</div>{locked && <small className={s.lockedNote}>已锁账 PDF 为固定快照，版式选项不可更改。</small>}</section>}
            <section className={s.tabletPaper}><div><h3>纸张与页数</h3><p>A4 · {variant === 'detail' ? '纵向' : '横向'}</p></div><div><h3>预计页数</h3><p>{pageCount} 页</p></div></section>
            <section className={`${s.tabletState} ${locked ? s.tabletStateLocked : s.tabletStateDraft}`}>
              <strong>{locked ? '✓ 当前是「已锁账」状态，可以导出正式版' : `⚠ ${data.status === 'CONFIRMED' ? '已确认' : '草稿'}只能导出预览版`}</strong>
              <p>{locked ? '正式 PDF 使用已锁账快照；解锁或修改业务单据不会更新已导出的文件。' : '预览版 PDF 会打水印「草稿 · 仅供核对」，锁账后才能导出正式版。'}</p>
            </section>
            {error && <p className={s.error} role="alert">{error}</p>}
            {previewError && <p className={s.error} role="alert">{previewError}</p>}
          </aside>
          <main className={s.tabletPreview}>
            <div className={s.previewSurface} aria-label="PDF 页面预览">{readyFile && !previewError
              ? <canvas ref={canvasRef} aria-label={`PDF 第 ${previewPage} 页`} />
              : <div className={s.previewEmpty}>{previewError || error || '正在准备 PDF 预览…'}</div>}</div>
            <div className={s.previewControls}><button type="button" aria-label="上一页" disabled={!readyFile || previewPage <= 1} onClick={() => setPreviewPage((page) => Math.max(1, page - 1))}>‹</button><span>{previewPage} / {pageCount} 页</span><button type="button" aria-label="下一页" disabled={!readyFile || previewPage >= pageCount} onClick={() => setPreviewPage((page) => Math.min(pageCount, page + 1))}>›</button></div>
          </main>
        </div>
        <footer className={s.tabletFooter}><span>{variant === 'detail' ? '明细版' : '汇总版'} · {displayedRows} 行 · 预计 {pageCount} 页</span>
          <button type="button" onClick={onClose} disabled={busy}>取消</button>
          <button type="button" onClick={openPdf} disabled={!readyFile || busy}><Icon name="print" size={16} />打印…</button>
          <button type="button" onClick={() => download(true)} disabled={!readyFile || busy}><Icon name="share" size={16} />分享…</button>
          {locked && pdfUrl ? <button type="button" className={s.tabletPrimary} disabled={busy || !readyFile} onClick={() => download(false)}>{readyFile ? '下载 PDF' : '准备 PDF…'}</button>
            : preview ? <button type="button" className={s.tabletPrimary} disabled={busy || !readyFile} onClick={() => download(false)}>{readyFile ? '导出预览版' : '准备 PDF…'}</button>
              : locked && canGenerate && !readOnly ? <button type="button" className={s.tabletPrimary} disabled={busy} onClick={() => void generate()}>{busy ? '生成中…' : '生成正式 PDF'}</button>
                : locked && canGenerate ? <a className={s.tabletPrimary} href={legacyUrl}>在老版生成</a>
                  : <button type="button" className={s.tabletPrimary} disabled title={locked ? '需要生成 PDF 权限' : '需先锁账'}>导出正式 PDF</button>}
        </footer>
      </div>
    </section>
  </div>;

  if (ff === 'phone') return <div className={`${s.scrim} ${s.phoneScrim}`}>
    <section className={s.phoneDialog} role="dialog" aria-modal="true" aria-label={`客户对账单 ${data.statementNo || ''} · 明细版`}>
      <header className={s.phoneHeader}>
        <button type="button" onClick={onClose} disabled={busy}>完成</button>
        <h2>客户对账单<small>{data.statementNo} · 明细版</small></h2>
        <button type="button" aria-label="分享" disabled={!readyFile || busy} onClick={() => download(true)}><Icon name="share" size={20} /></button>
      </header>
      <div className={s.phoneTags}>
        <span>{locked ? '已锁账 · 正式版' : `${data.status === 'DRAFT' ? '草稿' : '未锁账'} · 仅供核对`}</span>
        <span>{shippedSnapshotComplete ? '按已发数量计价' : '已发数量快照未记录'}</span>
        {adjustmentCount > 0 && <span>含冲减明细 {adjustmentCount} 行</span>}
      </div>
      <main className={s.phonePreview}>
        <div className={s.mobilePreview} aria-label="PDF 页面预览">
          <div className={s.mobilePreviewSurface}>{readyFile && !previewError
            ? <canvas ref={canvasRef} aria-label={`PDF 第 ${previewPage} 页`} />
            : <div className={s.previewEmpty}>{previewError || error || '正在准备 PDF 预览…'}</div>}</div>
          <div className={s.mobilePreviewControls}>
            <button type="button" aria-label="上一页" disabled={!readyFile || previewPage <= 1} onClick={() => setPreviewPage((page) => Math.max(1, page - 1))}>‹</button>
            <span>{previewPage} / {pageCount} 页 · 双指放大</span>
            <button type="button" aria-label="下一页" disabled={!readyFile || previewPage >= pageCount} onClick={() => setPreviewPage((page) => Math.min(pageCount, page + 1))}>›</button>
          </div>
        </div>
      </main>
      <footer className={s.phoneFooter}>
        <button type="button" disabled={!readyFile || busy} onClick={openPdf}><Icon name="print" size={18} />打印</button>
        <button type="button" className={s.phonePrimary} disabled={!readyFile || busy} onClick={() => download(true)}><Icon name="share" size={18} />发给客户核对</button>
      </footer>
    </section>
  </div>;

  return <div className={s.scrim} onMouseDown={(event) => { if (event.target === event.currentTarget && !busy) onClose(); }}>
    <section className={s.dialog} role="dialog" aria-modal="true" aria-label={`导出对账单 PDF ${data.statementNo || ''}`}>
      <header><h2>导出对账单 PDF</h2><button type="button" aria-label="关闭" disabled={busy} onClick={onClose}>×</button></header>
      <div className={s.body}>
        <div className={s.settings}>
        <div className={s.field}>
          <label><i>*</i>版式</label>
          <div className={s.radios} role="radiogroup" aria-label="PDF 版式">
            <button type="button" role="radio" aria-checked={variant === 'summary'} className={variant === 'summary' ? s.radioOn : ''} onClick={() => setVariant('summary')}><span />汇总版（单据级）</button>
            <button type="button" role="radio" aria-checked={variant === 'detail'} className={variant === 'detail' ? s.radioOn : ''} disabled={!canDetail} onClick={() => setVariant('detail')}><span />明细版（含货物）</button>
          </div>
          <p className={s.tip}>汇总版给财务内部核数；明细版发给客户签字</p>
        </div>

        <div className={s.mobilePreview} aria-label="PDF 页面预览">
          <div className={s.mobilePreviewSurface}>{readyFile && !previewError
            ? <canvas ref={canvasRef} aria-label={`PDF 第 ${previewPage} 页`} />
            : <div className={s.previewEmpty}>{previewError || error || '正在准备 PDF 预览…'}</div>}</div>
          <div className={s.mobilePreviewControls}>
            <button type="button" aria-label="上一页" disabled={!readyFile || previewPage <= 1} onClick={() => setPreviewPage((page) => Math.max(1, page - 1))}>‹</button>
            <span>{previewPage} / {pageCount} 页</span>
            <button type="button" aria-label="下一页" disabled={!readyFile || previewPage >= pageCount} onClick={() => setPreviewPage((page) => Math.min(pageCount, page + 1))}>›</button>
          </div>
        </div>

        {variant === 'detail' && <div className={`${s.field} ${s.detailOptions}`}>
          <label>明细版选项</label>
          <div>{detailOptionLabels.map((label, index) => <button key={label} type="button" aria-pressed={detailOptions[index]} disabled={locked} onClick={() => toggleDetailOption(index)}>
            <span className={`${s.switch} ${detailOptions[index] ? s.switchOn : ''}`} />{label}
          </button>)}</div>
        </div>}

        <div className={`${s.field} ${s.paperFields}`}>
          <div className={s.paperField}><label htmlFor="statement-paper">纸张</label><select id="statement-paper" value={variant} onChange={() => undefined} aria-label="纸张方向" disabled>
            <option value="summary">A4 · 横向</option><option value="detail">A4 · 纵向</option>
          </select><span className={s.chevron} aria-hidden="true">›</span></div>
          <div className={s.paperField}><label>预计页数</label><div className={s.pageCount}>{pageCount} 页</div></div>
        </div>

        {preview && <div className={`${s.notice} ${s.danger}`}>
          <span className={s.noticeIcon}>⚠</span><div><strong>当前是「{data.status === 'CONFIRMED' ? '已确认' : '草稿'}」状态，只能导出预览版</strong>
            <p>预览版 PDF 会打水印「{data.status === 'DRAFT' ? '草稿' : '预览'} · 仅供核对」。<b>锁账后</b>才能导出正式版。</p></div>
        </div>}
        {locked && <div className={`${s.notice} ${s.success}`}>
          <span className={s.noticeIcon}>✓</span><div><strong>当前是「已锁账」状态，可以导出正式版</strong>
            <p>正式 PDF 使用已锁账快照；解锁或修改业务单据不会更新已导出的文件。</p></div>
        </div>}
        {data.status === 'CANCELLED' && <div className={`${s.notice} ${s.danger}`}>
          <span className={s.noticeIcon}>⚠</span><div><strong>这份对账单已作废，不能再导出 PDF。</strong></div>
        </div>}
        {locked && !pdfUrl && !readOnly && <p className={s.noticeText}>尚未生成当前版式的正式 PDF。</p>}
        {locked && !pdfUrl && readOnly && <p className={s.noticeText}>生产 V2 目前只读，请在老版生成正式 PDF。</p>}
        {pdfPath && !pdfUrl && <p className={s.noticeText}>归档地址无效，请在老版检查这份 PDF。</p>}
        {error && <p className={s.error} role="alert">{error}</p>}
        </div>
      </div>
      <footer><span>{variant === 'detail' ? '明细版' : '汇总版'} · {displayedRows} 行 · 预计 {pageCount} 页</span><button type="button" onClick={onClose} disabled={busy}>取消</button>
        {locked && pdfUrl ? <>
          {error && !readyFile ? <button type="button" onClick={() => setPdfRetry((value) => value + 1)}>重试 PDF</button> : null}
          <button type="button" className={s.primary} disabled={busy || !readyFile} onClick={() => download(false)}>{readyFile ? '下载 PDF' : '准备 PDF…'}</button>
          <button type="button" disabled={busy || !readyFile} onClick={() => download(true)}>分享 / 存储</button>
        </> : preview ? <button type="button" className={s.primary} disabled={busy || !readyFile} onClick={() => download(false)}>{readyFile ? '导出预览版' : '准备 PDF…'}</button>
          : locked && canGenerate && !readOnly ? <button type="button" className={s.primary} disabled={busy} onClick={() => void generate()}>{busy ? '生成中…' : '生成正式 PDF'}</button>
            : locked && canGenerate ? <a className={s.primary} href={legacyUrl}>在老版生成</a>
              : <button type="button" disabled title={locked ? '需要生成 PDF 权限' : '需先锁账'}>导出正式 PDF</button>}
      </footer>
    </section>
  </div>;
}
